import { Button } from '@optools/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@optools/components/ui/sheet';
import { Tooltip, TooltipContent, TooltipTrigger } from '@optools/components/ui/tooltip';
import { LoggerGamePipeline } from '@optools/services/operator-gateway/logger/types';
import { Feed, MediaFeedType } from '@optools/services/operator-gateway/types/generated/graphql';
import { Copy, Info } from 'lucide-react';

const toClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
};

interface CopyButtonProps {
  ariaLabel: string;
  content: string;
  onClickHandler: () => void;
}

function CopyButton({ ariaLabel, content, onClickHandler }: CopyButtonProps) {
  return (
    <div className="flex items-center">
      <Button
        aria-label={ariaLabel}
        className="gap-2"
        size="sm"
        variant="outline"
        onClick={onClickHandler}>
        {content}
        <Copy />
      </Button>
    </div>
  );
}

export interface FeedInfoPanelProps {
  feed?: Feed;
  mediaId: string;
  pipeline: LoggerGamePipeline;
  side: MediaFeedType;
}

export function FeedInfoPanel({ feed, mediaId, pipeline, side }: FeedInfoPanelProps) {
  const {
    activeSlateCode,
    feedType,
    id,
    programSource,
    resumedFromDateTime,
    scheduledStartDateTime,
    sourceSystem,
    status,
    streamState,
  } = pipeline;
  return (
    <Sheet>
      <Tooltip>
        <SheetTrigger asChild>
          <TooltipTrigger asChild>
            <Button aria-label="info" className="size-6" size="icon" variant="ghost">
              <Info />
            </Button>
          </TooltipTrigger>
        </SheetTrigger>
        <TooltipContent side="bottom">
          <p className="text-xs">Debug Info</p>
        </TooltipContent>
      </Tooltip>

      <SheetContent className="w-125" side={side === MediaFeedType.AWAY ? 'left' : 'right'}>
        <SheetHeader>
          <SheetTitle className="mt-2 self-start">Feed Info:</SheetTitle>
          <SheetDescription className="sr-only">feed information for debugging</SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-2">
          <CopyButton
            ariaLabel="pipeline id"
            content={`ID: ${id}`}
            onClickHandler={() => toClipboard(id.toString())}
          />
          <CopyButton
            ariaLabel="media id"
            content={`Media ID: ${mediaId}`}
            onClickHandler={() => toClipboard(mediaId)}
          />
          <CopyButton
            ariaLabel="feed type"
            content={`Feed Type: ${feedType}`}
            onClickHandler={() => toClipboard(feedType)}
          />
          <CopyButton
            ariaLabel="program source"
            content={`Program Source: ${programSource}`}
            onClickHandler={() => toClipboard(programSource)}
          />
          <CopyButton
            ariaLabel="source system"
            content={`Source System: ${sourceSystem}`}
            onClickHandler={() => toClipboard(sourceSystem.toString())}
          />
          <CopyButton
            ariaLabel="active slate code"
            content={`Active Slate Code: ${activeSlateCode || 'NA'}`}
            onClickHandler={() => toClipboard(activeSlateCode || '')}
          />
          <CopyButton
            ariaLabel="pipeline status"
            content={`Status: ${status}`}
            onClickHandler={() => toClipboard(status)}
          />
          <CopyButton
            ariaLabel="stream state"
            content={`Stream State: ${streamState}`}
            onClickHandler={() => toClipboard(streamState)}
          />
          <CopyButton
            ariaLabel="resumed date"
            content={`Resumed Date/Time: ${resumedFromDateTime ? resumedFromDateTime : 'NA'}`}
            onClickHandler={() => toClipboard(resumedFromDateTime || '')}
          />
          <CopyButton
            ariaLabel="pre-game show date"
            content={`Scheduled Pre-Game Show Date/Time: ${
              feed?.preGameShow ? feed?.preGameShow : 'NA'
            }`}
            onClickHandler={() => toClipboard(feed?.preGameShow ?? '')}
          />
          <CopyButton
            ariaLabel="scheduled start date"
            content={`Scheduled Start Date/Time: ${
              scheduledStartDateTime ? scheduledStartDateTime : 'NA'
            }`}
            onClickHandler={() => toClipboard(scheduledStartDateTime ?? '')}
          />
          <CopyButton
            ariaLabel="post-game show"
            content={`Post-Game Show: ${feed?.postGameShow ?? 'false'}`}
            onClickHandler={() => toClipboard(feed?.postGameShow.toString() ?? 'false')}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
